// ── StarChart helpers ───────────────────────────────────────────────────────
// Pure logic behind the StarChart module: resolution choice, series statistics, normalisation,
// CSV export, and the small parsing quirks of the upstream API.

import { format } from 'date-fns';
import type { ChartPoint } from './chartMath';
import type {
  NormalizationMode,
  PopularityWindow,
  StarchartHealthResponse,
  StarchartResolution,
  StarchartServedResolution,
} from '../types/starchart';

/** Local time is the ARGUS default; UTC is a persisted opt-in for cross-region comparison. */
export type TimeZoneMode = 'local' | 'utc';

export const TIMEZONE_STORAGE_KEY = 'starchart.timezone';

// ── Ranges and resolution ───────────────────────────────────────────────────

export interface RangePreset {
  id: string;
  label: string;
  ms: number;
}

/**
 * A year genuinely works: hourly data reaches back 12 months (verified — 8 687 points, 77 KB,
 * 1.8 s). Do not clamp the picker to `/db/info.earliest_timestamp`, which describes the raw
 * table only and understates coverage by an order of magnitude.
 */
export const RANGE_PRESETS: RangePreset[] = [
  { id: '6h', label: '6h', ms: 6 * 3600_000 },
  { id: '24h', label: '24h', ms: 24 * 3600_000 },
  { id: '7d', label: '7d', ms: 7 * 86400_000 },
  { id: '30d', label: '30d', ms: 30 * 86400_000 },
  { id: '90d', label: '90d', ms: 90 * 86400_000 },
  { id: '1y', label: '1y', ms: 365 * 86400_000 },
];

export const MIN_RANGE_MS = 5 * 60_000;
export const MAX_RANGE_MS = 366 * 86400_000;

/** Sampling interval, in ms, of each stored resolution. */
export const RESOLUTION_INTERVAL_MS: Record<StarchartServedResolution, number> = {
  raw: 60_000,
  '15m': 900_000,
  hourly: 3_600_000,
};

/** Past this many points a request is slow enough to warn about before sending it. */
export const HEAVY_POINT_THRESHOLD = 20_000;

/**
 * Choose a resolution client-side rather than sending `resolution=auto`.
 *
 * `auto` is a trap for wide ranges: a 30-day request returns `raw`, which is 36 000 points and
 * 1.6 MB uncompressed. Deciding here keeps every default request small and predictable.
 */
export const resolveResolution = (
  spanMs: number,
  preference: StarchartResolution = 'auto',
): StarchartServedResolution => {
  if (preference !== 'auto') return preference;
  if (spanMs <= 48 * 3600_000) return 'raw';
  if (spanMs <= 30 * 86400_000) return '15m';
  return 'hourly';
};

/** Roughly how many samples a range will return at a resolution. Drives the heavy-range warning. */
export const estimatePointCount = (
  spanMs: number,
  resolution: StarchartServedResolution,
): number => Math.max(0, Math.round(spanMs / RESOLUTION_INTERVAL_MS[resolution]));

/**
 * Floor both bounds to the resolution bucket.
 *
 * Two jobs: identical requests from different components produce one cache key, and a live "now"
 * end-bound stops busting the cache on every render.
 */
export const quantizeRange = (
  startMs: number,
  endMs: number,
  resolution: StarchartServedResolution,
): { start: Date; end: Date } => {
  const bucket = RESOLUTION_INTERVAL_MS[resolution];
  return {
    start: new Date(Math.floor(startMs / bucket) * bucket),
    end: new Date(Math.ceil(endMs / bucket) * bucket),
  };
};

// ── Timestamps ──────────────────────────────────────────────────────────────

/**
 * Parse the API's `current_time` ("2026-09-03 10:43:25"), which is UTC but carries no marker.
 * `new Date("2026-09-03 10:43:25")` parses as *local* and is silently hours wrong.
 */
export const parseStarchartClock = (value: string): number | null => {
  if (!value) return null;
  const parsed = Date.parse(`${value.trim().replace(' ', 'T')}Z`);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Difference between our clock and the API's, in seconds.
 *
 * `health.uptime_seconds` is neither an uptime nor a start time — it is the API's own current
 * clock, so the only honest thing to derive from it is skew. The standalone site treated it as a
 * start timestamp and therefore always displayed "0m" of uptime.
 */
export const apiClockSkewSeconds = (
  health: StarchartHealthResponse,
  nowMs: number = Date.now(),
): number => Math.round(nowMs / 1000) - health.uptime_seconds;

/** Value for a `datetime-local` input, in the active zone. */
export const formatDateTimeInput = (date: Date, mode: TimeZoneMode): string => {
  if (mode === 'utc') {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
      `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
    );
  }
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

/** Inverse of `formatDateTimeInput`. The input carries no zone, so the mode decides. */
export const parseDateTimeInput = (value: string, mode: TimeZoneMode): Date | null => {
  if (!value) return null;
  const parsed = mode === 'utc' ? Date.parse(`${value}Z`) : Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

/** Axis and tooltip formatter honouring the active zone. */
export const formatChartTime = (ms: number, pattern: string, mode: TimeZoneMode): string => {
  if (mode === 'utc') {
    // Shift by the offset so date-fns formats the UTC wall clock.
    const shifted = new Date(ms + new Date(ms).getTimezoneOffset() * 60_000);
    return format(shifted, pattern);
  }
  return format(new Date(ms), pattern);
};

/** A date-fns pattern proportionate to the visible span. */
export const timeLabelPattern = (spanMs: number): string => {
  if (spanMs <= 2 * 86400_000) return 'HH:mm';
  if (spanMs <= 14 * 86400_000) return 'MMM d HH:mm';
  if (spanMs <= 180 * 86400_000) return 'MMM d';
  return 'MMM yyyy';
};

// ── Series statistics ───────────────────────────────────────────────────────

export interface SeriesStats {
  count: number;
  /** Samples that carried a value. Nulls are excluded, never counted as zero. */
  valueCount: number;
  min: number | null;
  max: number | null;
  average: number | null;
  latest: number | null;
  first: number | null;
  /** Population standard deviation. */
  stdDev: number | null;
}

/**
 * Summarise a series, tolerating empty and all-null input.
 *
 * `Math.max(...[])` is `-Infinity`, which the standalone site would happily render into the DOM
 * for an empty range. Everything here returns null instead.
 */
export const computeSeriesStats = (points: ChartPoint[]): SeriesStats => {
  const values: number[] = [];
  for (const point of points) {
    if (point.v !== null && isFinite(point.v)) values.push(point.v);
  }

  if (values.length === 0) {
    return {
      count: points.length,
      valueCount: 0,
      min: null,
      max: null,
      average: null,
      latest: null,
      first: null,
      stdDev: null,
    };
  }

  let min = values[0];
  let max = values[0];
  let sum = 0;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
    sum += value;
  }
  const average = sum / values.length;

  let variance = 0;
  for (const value of values) variance += (value - average) ** 2;
  variance /= values.length;

  return {
    count: points.length,
    valueCount: values.length,
    min,
    max,
    average,
    latest: values[values.length - 1],
    first: values[0],
    stdDev: Math.sqrt(variance),
  };
};

/**
 * How far apart two series' magnitudes are, as a ratio of the larger to the smaller.
 *
 * Used to decide whether overlaying them on one axis is legible. The network total runs to a
 * hundred-odd players while a single server often sits under five, so drawn raw on a shared axis
 * the server's line flattens onto the floor and the comparison tells you nothing. Returns 1 when
 * either series has no usable maximum, so a missing series never triggers a rescale.
 */
export const scaleMismatchRatio = (a: ChartPoint[], b: ChartPoint[]): number => {
  const maxOf = (points: ChartPoint[]): number | null => {
    let max: number | null = null;
    for (const point of points) {
      if (point.v === null || !isFinite(point.v)) continue;
      if (max === null || point.v > max) max = point.v;
    }
    return max;
  };

  const maxA = maxOf(a);
  const maxB = maxOf(b);
  if (maxA === null || maxB === null) return 1;

  const larger = Math.max(Math.abs(maxA), Math.abs(maxB));
  const smaller = Math.min(Math.abs(maxA), Math.abs(maxB));
  // Zero on one side is an infinite ratio in principle; treat it as mismatched but finite.
  if (smaller === 0) return larger === 0 ? 1 : Infinity;
  return larger / smaller;
};

/** Past this ratio two series stop being readable on one shared axis. */
export const SCALE_MISMATCH_THRESHOLD = 5;

// ── Normalisation ───────────────────────────────────────────────────────────

export const NORMALIZATION_LABELS: Record<NormalizationMode, string> = {
  none: 'Raw data',
  'moving-average': 'Moving average',
  'min-max': 'Min-max (0-1)',
  'z-score': 'Z-score',
  'percentage-change': '% change',
};

export const NORMALIZATION_DESCRIPTIONS: Record<NormalizationMode, string> = {
  none: 'Values exactly as recorded.',
  'moving-average': 'Averages nearby samples so the trend reads through the noise.',
  'min-max': 'Rescales each series to 0-1 so shapes can be compared regardless of scale.',
  'z-score': 'Standard deviations from that series own mean. Around 0 is typical.',
  'percentage-change': 'Change from the first sample, so relative growth is comparable.',
};

/**
 * Transform a series for display. Nulls pass through untouched so gaps survive every mode.
 *
 * Statistics are computed over the non-null values only — treating a gap as zero would drag a
 * mean down and make an outage look like a quiet period.
 */
export const normalizeSeries = (
  points: ChartPoint[],
  mode: NormalizationMode,
  movingAverageWindow = 5,
): ChartPoint[] => {
  if (mode === 'none' || points.length === 0) return points;

  if (mode === 'moving-average') {
    const half = Math.max(1, Math.floor(movingAverageWindow / 2));
    return points.map((point, i) => {
      if (point.v === null) return point;
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - half); j <= Math.min(points.length - 1, i + half); j += 1) {
        const value = points[j].v;
        if (value !== null && isFinite(value)) {
          sum += value;
          count += 1;
        }
      }
      return { t: point.t, v: count > 0 ? sum / count : null };
    });
  }

  const stats = computeSeriesStats(points);
  if (stats.valueCount === 0) return points;

  if (mode === 'min-max') {
    const span = (stats.max ?? 0) - (stats.min ?? 0);
    // A flat series has no meaningful position within its own range; centre it.
    if (span === 0) return points.map((p) => ({ t: p.t, v: p.v === null ? null : 0.5 }));
    return points.map((p) => ({
      t: p.t,
      v: p.v === null ? null : (p.v - (stats.min ?? 0)) / span,
    }));
  }

  if (mode === 'z-score') {
    const stdDev = stats.stdDev ?? 0;
    if (stdDev === 0) return points.map((p) => ({ t: p.t, v: p.v === null ? null : 0 }));
    return points.map((p) => ({
      t: p.t,
      v: p.v === null ? null : (p.v - (stats.average ?? 0)) / stdDev,
    }));
  }

  // percentage-change
  const baseline = stats.first ?? 0;
  if (baseline === 0) {
    // Growth from nothing is undefined as a percentage; show the absolute value scaled instead.
    return points.map((p) => ({ t: p.t, v: p.v === null ? null : p.v * 100 }));
  }
  return points.map((p) => ({
    t: p.t,
    v: p.v === null ? null : ((p.v - baseline) / baseline) * 100,
  }));
};

// ── Performance banding ─────────────────────────────────────────────────────

export type PerformanceBand = 'excellent' | 'good' | 'warning' | 'critical' | 'unknown';

export const PERFORMANCE_BAND_LABELS: Record<PerformanceBand, string> = {
  excellent: 'Excellent',
  good: 'Good',
  warning: 'Warning',
  critical: 'Critical',
  unknown: 'No data',
};

/** Badge tone for a band, using the shared semantic mapping. */
export const performanceBandTone = (band: PerformanceBand): string => {
  switch (band) {
    case 'excellent':
      return 'success';
    case 'good':
      return 'info';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'danger';
    default:
      return 'neutral';
  }
};

/**
 * Band a server's tick performance. TPS is preferred when present because it is the number
 * staff quote; MSPT is the fallback. Thresholds match the standalone site so nobody has to
 * relearn what "good" means.
 */
export const performanceBand = (mspt: number | null, tps: number | null): PerformanceBand => {
  if (tps !== null && isFinite(tps)) {
    if (tps >= 19.5) return 'excellent';
    if (tps >= 18) return 'good';
    if (tps >= 15) return 'warning';
    return 'critical';
  }
  if (mspt !== null && isFinite(mspt)) {
    if (mspt <= 50) return 'excellent';
    if (mspt <= 55) return 'good';
    if (mspt <= 66) return 'warning';
    return 'critical';
  }
  return 'unknown';
};

// ── Server id handling ──────────────────────────────────────────────────────

export interface CaseVariantReport {
  /** Ids to show normally. */
  canonical: string[];
  /** Ids whose lowercase twin is tracked far more heavily — real, but near-empty, series. */
  shadowed: string[];
}

/**
 * Split ids that differ only by case.
 *
 * `PROJINF.playcdu.co` (75 snapshots) and `projinf.playcdu.co` (13 101) are two genuinely
 * distinct upstream series and both are separately ranked, so neither may be dropped. The
 * lighter one is flagged so the table can mark it rather than confuse it with the real server.
 */
export const partitionCaseVariants = (
  serverIds: string[],
  weightOf: (id: string) => number,
): CaseVariantReport => {
  const byLower = new Map<string, string[]>();
  for (const id of serverIds) {
    const key = id.toLowerCase();
    const bucket = byLower.get(key);
    if (bucket) bucket.push(id);
    else byLower.set(key, [id]);
  }

  const canonical: string[] = [];
  const shadowed: string[] = [];

  for (const variants of byLower.values()) {
    if (variants.length === 1) {
      canonical.push(variants[0]);
      continue;
    }
    const sorted = [...variants].sort((a, b) => weightOf(b) - weightOf(a));
    canonical.push(sorted[0]);
    shadowed.push(...sorted.slice(1));
  }

  return { canonical, shadowed };
};

// ── CSV export ──────────────────────────────────────────────────────────────

export interface CsvSeries {
  id: string;
  label: string;
  points: ChartPoint[];
}

/**
 * Build a CSV over the union of every series' timestamps.
 *
 * The standalone site indexed overlays positionally (`overlay.data[i]`), which silently
 * misaligns the moment two servers have different sample counts — which happens whenever one of
 * them was down. Keying by timestamp makes that impossible.
 */
export const buildStarchartCsv = (series: CsvSeries[], zone: TimeZoneMode = 'utc'): string => {
  const timestamps = new Set<number>();
  const lookup = new Map<string, Map<number, number | null>>();

  for (const entry of series) {
    const byTime = new Map<number, number | null>();
    for (const point of entry.points) {
      timestamps.add(point.t);
      byTime.set(point.t, point.v);
    }
    lookup.set(entry.id, byTime);
  }

  const ordered = Array.from(timestamps).sort((a, b) => a - b);
  const escape = (value: string) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

  const header = ['timestamp_unix', 'timestamp_iso', ...series.map((s) => escape(s.label))].join(',');

  const rows = ordered.map((t) => {
    const cells = [
      String(Math.floor(t / 1000)),
      zone === 'utc' ? new Date(t).toISOString() : format(new Date(t), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    ];
    for (const entry of series) {
      const value = lookup.get(entry.id)?.get(t);
      cells.push(value === undefined || value === null ? '' : String(value));
    }
    return cells.join(',');
  });

  return [header, ...rows].join('\n');
};

// ── Formatting ──────────────────────────────────────────────────────────────

export const formatPopularity = (value: number | null | undefined): string =>
  value === null || value === undefined || !isFinite(value) ? '—' : value.toFixed(1);

export const formatTps = (value: number | null | undefined): string =>
  value === null || value === undefined || !isFinite(value) ? '—' : value.toFixed(2);

export const formatMspt = (value: number | null | undefined): string =>
  value === null || value === undefined || !isFinite(value) ? '—' : `${value.toFixed(1)}ms`;

export const formatPlayers = (value: number | null | undefined): string =>
  value === null || value === undefined || !isFinite(value) ? '—' : Math.round(value).toLocaleString();

/** Compact duration for session totals, which arrive in seconds and reach into months. */
export const formatDurationSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || !isFinite(seconds) || seconds <= 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const POPULARITY_WINDOW_LABELS: Record<PopularityWindow, string> = {
  '24h': '24 hour',
  '7d': '7 day',
  '28d': '28 day',
};
